import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { v4 } from 'uuid';
import { doc, setDoc, updateDoc } from 'firebase/firestore';

import { DB } from '~/services/firebase';
import getValidationErrors from '~/utils/getValidationErrors';
import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';
import ICompany from '~/interfaces/ICompany';
import { bff } from '~/services/bff';

interface SettingsCompanyProps {
  openMenu(): void;
}

const { REACT_APP_FIREBASE_PROJECT } = process.env;

const SettingsCompany: React.FC<SettingsCompanyProps> = ({ openMenu }) => {
  const { user, company, updateCompany } = useAuth();
  const { addToast } = useToast();
  const [data, setData] = useState<ICompany>(company);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (company && company.uid) setData(company);
  }, [company]);

  // useEffect(() => {
  //   async function loadCompany() {
  //     const companiesRef = collection(DB, 'companies');
  //     const q = query(companiesRef, where('user_id', '==', user.uid));
  //     const querySnapshot = await getDocs(q);

  //     if (querySnapshot.docs.length > 0) {
  //       const aux = querySnapshot.docs[0].data() as ICompany;
  //       setData({
  //         ...aux,
  //         uid: querySnapshot.docs[0].id,
  //       });
  //     }
  //   }

  //   loadCompany();
  // }, [user.uid]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        setErrors({});

        const schema = Yup.object().shape({
          fantasy: Yup.string().required('O campo fantasia é obrigatório'),
          // social_reason: Yup.string().required(
          //   'O campo razão social é obrigatório',
          // ),
          phone: Yup.string().required('O campo telefone é obrigatório'),
          // email: Yup.string()
          //   .required('O campo e-mail é obrigatório')
          //   .email('Informe um e-mail válido'),
          // address: Yup.string().required('O campo endereço é obrigatório'),
          // waiting_time: Yup.string().required(
          //   'O campo tempo de espera é obrigatório',
          // ),
          shipping_cost: Yup.string().required(
            'O campo valor de frete é obrigatório',
          ),
          // service_hours: Yup.string().required(
          //   'O campo horário de atendimento é obrigatório',
          // ),
          // minimum_order: Yup.string().required(
          //   'O campo pedido mínimo é obrigatório',
          // ),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const uid = data.uid || v4();

        const parseData = {
          ...data,
          uid,
          user_id: user.uid,
        };

        if (data.uid) {
          await updateDoc(doc(DB, 'companies', uid), parseData);
        } else {
          await setDoc(doc(DB, 'companies', uid), parseData);
        }

        await bff.delete(`/companies/${REACT_APP_FIREBASE_PROJECT}`);

        updateCompany(parseData);

        addToast({
          type: 'success',
          description: 'As configurações da empresa foram salvas com sucesso!',
        });

        setLoading(false);
      } catch (err: any) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
          return;
        }

        addToast({
          type: 'danger',
          description:
            'Infelizmente não foi possível completar a operação. Por favor, Tente novamente.',
        });
      }
    },
    [data, user, addToast, updateCompany],
  );

  return (
    <div className="nk-block">
      <div className="nk-block-head nk-block-between">
        <div className="nk-block-head-content">
          <h5 className="nk-block-title">Empresa</h5>
          <div className="nk-block-des">
            <p>
              Configure as informações da sua empresa de acordo como deseja
              apresentá-la.
            </p>
          </div>
        </div>
        <div className="nk-block-head-content align-self-start d-lg-none">
          <Link
            to="#menu"
            className="toggle btn btn-icon btn-trigger mt-n1"
            data-target="userAside"
            onClick={openMenu}
          >
            <em className="icon ni ni-menu-alt-r" />
          </Link>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group mb-3">
              <label className="form-label">Fantasia</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="Fantasia"
                  defaultValue={data.fantasy}
                  onChange={event => {
                    setData({
                      ...data,
                      fantasy: event.target.value,
                    });
                  }}
                />

                <span className="fs-9px">
                  * Nome que será apresentado ao cliente
                </span>
                <br />
                {errors.fantasy && (
                  <span className="invalid">{errors.fantasy}</span>
                )}
              </div>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group mb-3">
              <label className="form-label">Razão Social</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="Razão Social"
                  defaultValue={data.social_reason}
                  onChange={event => {
                    setData({
                      ...data,
                      social_reason: event.target.value,
                    });
                  }}
                />
                {errors.social_reason && (
                  <span className="invalid">{errors.social_reason}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-3">
            <div className="form-group mb-3">
              <label className="form-label">CNPJ</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="99.999.999/9999-99"
                  defaultValue={data.cnpj}
                  onChange={event => {
                    setData({
                      ...data,
                      cnpj: event.target.value,
                    });
                  }}
                />
                {errors.cnpj && <span className="invalid">{errors.cnpj}</span>}
              </div>
            </div>
          </div>

          <div className="col-sm-3">
            <div className="form-group mb-3">
              <label className="form-label">Telefone</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="(99) 99999-9999"
                  defaultValue={data.phone}
                  onChange={event => {
                    setData({
                      ...data,
                      phone: event.target.value,
                    });
                  }}
                />
                {errors.phone && (
                  <span className="invalid">{errors.phone}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group mb-3">
              <label className="form-label">E-mail</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="email@email.com"
                  defaultValue={data.email}
                  onChange={event => {
                    setData({
                      ...data,
                      email: event.target.value,
                    });
                  }}
                />
                {errors.email && (
                  <span className="invalid">{errors.email}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group mb-3">
              <label className="form-label">Instagram</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="Link para o instagram"
                  defaultValue={data.instagram}
                  onChange={event => {
                    setData({
                      ...data,
                      instagram: event.target.value,
                    });
                  }}
                />
                {errors.instagram && (
                  <span className="invalid">{errors.instagram}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group mb-3">
              <label className="form-label">Facebook</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="Link para o Facebook"
                  defaultValue={data.facebook}
                  onChange={event => {
                    setData({
                      ...data,
                      facebook: event.target.value,
                    });
                  }}
                />
                {errors.facebook && (
                  <span className="invalid">{errors.facebook}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-3">
            <div className="form-group mb-3">
              <label className="form-label">Latitude</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="Latitude"
                  defaultValue={data.latitude}
                  onChange={event => {
                    setData({
                      ...data,
                      latitude: event.target.value,
                    });
                  }}
                />
                {errors.latitude && (
                  <span className="invalid">{errors.latitude}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-3">
            <div className="form-group mb-3">
              <label className="form-label">Longitude</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="Longitude"
                  defaultValue={data.longitude}
                  onChange={event => {
                    setData({
                      ...data,
                      longitude: event.target.value,
                    });
                  }}
                />
                {errors.longitude && (
                  <span className="invalid">{errors.longitude}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group mb-3">
              <label className="form-label">Endereço</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="Endereço completo da empresa"
                  defaultValue={data.address}
                  onChange={event => {
                    setData({
                      ...data,
                      address: event.target.value,
                    });
                  }}
                />
                {errors.address && (
                  <span className="invalid">{errors.address}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group mb-3">
              <label className="form-label">Tempo de Entrega</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="50 minutos"
                  defaultValue={data.waiting_time}
                  onChange={event => {
                    setData({
                      ...data,
                      waiting_time: event.target.value,
                    });
                  }}
                />
                <span className="fs-9px">
                  * Tempo para produzir e entregar o pedido.
                </span>
                <br />
                {errors.waiting_time && (
                  <span className="invalid">{errors.waiting_time}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-3">
            <div className="form-group mb-3">
              <label className="form-label">Valor da Entrega</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control form-control-lg"
                  placeholder="0,00"
                  defaultValue={data.shipping_cost}
                  onChange={event => {
                    setData({
                      ...data,
                      shipping_cost: event.target.value,
                    });
                  }}
                />
                {errors.shipping_cost && (
                  <span className="invalid">{errors.shipping_cost}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-3">
            <div className="form-group mb-3">
              <label className="form-label">Pedido Mínimo</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control form-control-lg"
                  placeholder="0,00"
                  defaultValue={data.minimum_order}
                  onChange={event => {
                    setData({
                      ...data,
                      minimum_order: event.target.value,
                    });
                  }}
                />
                {errors.minimum_order && (
                  <span className="invalid">{errors.minimum_order}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group mb-3">
              <label className="form-label">Mensagem aos clientes</label>
              <div className="form-control-wrap">
                <textarea
                  className="form-control no-resize"
                  placeholder="Deixe uma mensagem para seus clientes."
                  defaultValue={data.description}
                  onChange={event => {
                    setData({
                      ...data,
                      description: event.target.value,
                    });
                  }}
                />
              </div>
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group mb-3">
              <label className="form-label">Horários de Atendimento</label>
              <div className="form-control-wrap">
                <textarea
                  className="form-control no-resize"
                  placeholder="Segunda à Sexta de 08:00 às 18:00"
                  defaultValue={data.service_hours}
                  onChange={event => {
                    setData({
                      ...data,
                      service_hours: event.target.value,
                    });
                  }}
                />
                {errors.service_hours && (
                  <span className="invalid">{errors.service_hours}</span>
                )}
              </div>
            </div>
          </div>

          <div className="form-group col-sm-12">
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading}
            >
              {loading && (
                <div
                  className="spinner-border spinner-border-sm"
                  role="status"
                />
              )}
              {!loading && 'Salvar'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SettingsCompany;
